import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, Lock, Broadcast, ChartLineUp, Trophy, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Paywall() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState(5.0);
  const nav = useNavigate();
  const loc = useLocation();

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 10) return;
    try {
      const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
      if (data.subscription_status === "active" || data.subscription_status === "trialing") {
        toast("Subscription active — welcome to GP Picks Pro");
        await refresh();
        nav("/", { replace: true });
        return;
      }
    } catch (e) {
      // Polling error — will retry via setTimeout below. Log for debugging.
      console.debug("checkout-status poll error:", e);
    }
    setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
  }, [refresh, nav]);

  // Detect return from Stripe (?session_id=...)
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const sid = q.get("session_id");
    if (sid) {
      setBusy(true);
      pollStatus(sid);
    }
  }, [loc.search, pollStatus]);

  useEffect(() => {
    api.get("/billing/status").then(({ data }) => setPrice(data.price_usd || 5.0)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      toast.error("Could not start checkout", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-14">
      <div className="overline mb-3">Members Only</div>
      <h1 className="font-head text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">
        UNLOCK THE<br />
        <span style={{ color: "#FF4500" }}>FULL BOARD.</span>
      </h1>
      <p className="text-[#A1A1AA] max-w-xl mt-4 text-base">
        30+ daily player props scored by our confidence model. Live matchup adjustments from real sportsbook lines. Post-game
        accuracy tracking. Confidence ratings — <span className="text-white">not guaranteed picks</span>.
      </p>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 items-start mt-10">
        {/* Feature list */}
        <div className="border border-[#27272A] bg-[#121212] p-6">
          <div className="overline mb-4">What you get</div>
          <ul className="space-y-4">
            {[
              { i: Broadcast, t: "Live daily picks", d: "50+ props across MLB, NFL, NHL, NBA — refreshed regularly from real sportsbook lines." },
              { i: ChartLineUp, t: "Matchup-adjusted confidence ratings", d: "Every pick factors opposing pitcher ERA / team OPS from ESPN. Nothing is guaranteed — you see the model's confidence, not a promise." },
              { i: Trophy, t: "Results & accuracy tracking", d: "Post-game grading via Odds API scores + ESPN box scores. Track hit rates transparently — wins and losses both." },
              { i: CheckCircle, t: "Cancel anytime", d: "Stripe-managed billing. One click cancel from your account." },
            ].map(({ i: Icon, t, d }) => (
              <li key={t} className="flex gap-3">
                <Icon size={20} weight="fill" color="#FF4500" className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-head text-white text-base">{t}</div>
                  <div className="text-[#A1A1AA] text-sm">{d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Subscribe card */}
        <div className="border border-[#FF4500] bg-[#121212] p-6" data-testid="subscribe-card">
          <div className="overline text-[#FF4500]">GP Picks Pro</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-head text-6xl text-white leading-none">${price.toFixed(0)}</span>
            <span className="text-[#A1A1AA]">/month</span>
          </div>
          <p className="text-sm text-[#A1A1AA] mt-2">Billed monthly. Cancel anytime.</p>

          <button
            onClick={subscribe}
            disabled={busy}
            className="w-full mt-6 h-12 pick-over font-head uppercase tracking-wider text-base flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="subscribe-btn"
          >
            {busy ? "…" : (<>Subscribe <ArrowRight size={16} weight="bold" /></>)}
          </button>

          <div className="mt-4 text-xs text-[#52525b] flex items-center gap-1.5">
            <Lock size={11} /> Secure checkout by Stripe · Cancel anytime
          </div>
          <p className="mt-3 text-[10px] text-[#52525b] leading-relaxed border-t border-[#27272A] pt-3">
            <span className="text-[#FF4500] uppercase font-head">Disclaimer:</span> GP Picks is not a sportsbook. All picks
            are for informational purposes only — nothing is guaranteed. Any wagers you place are made entirely at your own risk. 18+.
          </p>
          {user === false && (
            <div className="mt-3 text-sm text-[#A1A1AA]">
              <Link to="/login" className="text-[#FF4500] hover:underline">Sign in</Link> if you already have an account.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
